import React, { createContext, useContext, useEffect, useRef, useState, useCallback } from 'react';
import { ZONES, IrrigationZone } from '../data/zones';

// ---- Types ----
export type ProgramType = 'irrigation' | 'fertigation';
export type ScheduleMode = 'weekly' | 'cyclic';

export interface TimeRange {
  id: string;
  start: string; // 'HH:MM'
  end: string; // 'HH:MM'
}

export interface IrrigationProgram {
  id: string;
  name: string;
  description: string;
  zone_id: string;
  type: ProgramType;
  mode: ScheduleMode;
  days: number[]; // 0=Lun ... 6=Dim (weekly)
  cycle_days: number; // cyclic
  time_ranges: TimeRange[];
  enabled: boolean;
  created_at: string;
  last_run?: string;
}

export interface ZoneSession {
  kind: 'manual' | 'scheduled';
  program_id?: string;
  program_name?: string;
  type: ProgramType;
  started_at: string; // ISO
  ends_at?: string; // ISO (scheduled only)
}

export interface ZoneRuntime {
  last_irrigated_at: string | null;
  session: ZoneSession | null;
}

export interface HistoryEvent {
  id: string;
  zone_id: string;
  zone_name: string;
  field_name: string;
  kind: 'manual' | 'scheduled';
  program_name?: string;
  type: ProgramType;
  started_at: string;
  ended_at: string;
  duration_minutes: number;
  water_liters: number;
  status: 'completed' | 'stopped';
}

// ---- Helpers (exported) ----
export const DAYS_FR = ['Lun', 'Mar', 'Mer', 'Jeu', 'Ven', 'Sam', 'Dim'];

export const toMinutes = (hhmm: string): number => {
  const [h, m] = hhmm.split(':').map((v) => parseInt(v, 10));
  return (h || 0) * 60 + (m || 0);
};

// Durée d'une plage en minutes (gère le passage à minuit, ex 23:00 -> 03:00)
export const rangeDurationMinutes = (r: TimeRange): number => {
  const s = toMinutes(r.start);
  const e = toMinutes(r.end);
  if (e === s) return 0;
  return e > s ? e - s : 1440 - s + e;
};

// Intervalles normalisés [a,b) sur 0..1440 (séparés si passage minuit)
const rangeIntervals = (r: TimeRange): Array<[number, number]> => {
  const s = toMinutes(r.start);
  const e = toMinutes(r.end);
  if (e > s) return [[s, e]];
  if (e < s) return [[s, 1440], [0, e]];
  return [];
};

// true si la liste de plages contient un chevauchement
export const rangesOverlap = (ranges: TimeRange[]): boolean => {
  const intervals: Array<[number, number]> = [];
  ranges.forEach((r) => rangeIntervals(r).forEach((iv) => intervals.push(iv)));
  intervals.sort((a, b) => a[0] - b[0]);
  for (let i = 1; i < intervals.length; i++) {
    if (intervals[i][0] < intervals[i - 1][1]) return true;
  }
  return false;
};

export const totalDailyMinutes = (ranges: TimeRange[]): number =>
  ranges.reduce((sum, r) => sum + rangeDurationMinutes(r), 0);

// Cellules de la timeline (24h). Renvoie 'active' | 'hot' | 'idle' par heure
export const HOT_START = 11;
export const HOT_END = 15;
export const timelineHours = (ranges: TimeRange[]): Array<'active' | 'hot' | 'idle'> => {
  const active = new Array(24).fill(false);
  ranges.forEach((r) => {
    rangeIntervals(r).forEach(([a, b]) => {
      for (let h = 0; h < 24; h++) {
        const hStart = h * 60;
        const hEnd = hStart + 60;
        if (a < hEnd && b > hStart) active[h] = true;
      }
    });
  });
  return active.map((isActive, h) => {
    if (isActive) return 'active';
    if (h >= HOT_START && h < HOT_END) return 'hot';
    return 'idle';
  });
};

// Nombre d'arrosages par semaine selon le mode
const runsPerWeek = (p: Pick<IrrigationProgram, 'mode' | 'days' | 'cycle_days'>): number => {
  if (p.mode === 'weekly') return p.days.length;
  return p.cycle_days > 0 ? 7 / p.cycle_days : 0;
};

export interface ImpactSim {
  waterPerWeek: number; // litres
  moisture: number; // % +
  plantImpact: 'Faible' | 'Moyen' | 'Élevé';
}

export const simulateImpact = (
  ranges: TimeRange[],
  meta: Pick<IrrigationProgram, 'mode' | 'days' | 'cycle_days'>,
  flowLpm: number
): ImpactSim => {
  const dailyMin = totalDailyMinutes(ranges);
  const perWeek = runsPerWeek(meta);
  const waterPerWeek = Math.round(dailyMin * flowLpm * perWeek);
  const moisture = Math.min(40, Math.round(dailyMin * perWeek * 0.05));

  // Impact plante : part des minutes tombant dans les heures chaudes
  let hotMin = 0;
  ranges.forEach((r) =>
    rangeIntervals(r).forEach(([a, b]) => {
      const hotA = HOT_START * 60;
      const hotB = HOT_END * 60;
      hotMin += Math.max(0, Math.min(b, hotB) - Math.max(a, hotA));
    })
  );
  let plantImpact: ImpactSim['plantImpact'] = 'Faible';
  if (dailyMin > 0) {
    const ratio = hotMin / dailyMin;
    if (ratio > 0.5) plantImpact = 'Élevé';
    else if (ratio > 0) plantImpact = 'Moyen';
  }
  return { waterPerWeek, moisture, plantImpact };
};

// Format compact d'une durée en ms -> "2j 3h", "4h 12m", "8m 03s", "12s"
export const formatDuration = (ms: number): string => {
  if (ms < 0) ms = 0;
  const totalSec = Math.floor(ms / 1000);
  const days = Math.floor(totalSec / 86400);
  const hours = Math.floor((totalSec % 86400) / 3600);
  const minutes = Math.floor((totalSec % 3600) / 60);
  const seconds = totalSec % 60;
  if (days > 0) return `${days}j ${hours}h`;
  if (hours > 0) return `${hours}h ${minutes.toString().padStart(2, '0')}m`;
  if (minutes > 0) return `${minutes}m ${seconds.toString().padStart(2, '0')}s`;
  return `${seconds}s`;
};

export const programScheduleLabel = (p: IrrigationProgram): string => {
  if (p.mode === 'weekly') {
    if (p.days.length === 7) return 'Tous les jours';
    if (p.days.length === 0) return 'Aucun jour sélectionné';
    return p.days
      .slice()
      .sort((a, b) => a - b)
      .map((d) => DAYS_FR[d])
      .join(', ');
  }
  return p.cycle_days === 1 ? 'Tous les jours' : `Tous les ${p.cycle_days} jours`;
};

const newId = (prefix: string) => `${prefix}-${Date.now()}-${Math.floor(Math.random() * 10000)}`;

// ---- Default seed ----
const nowIso = () => new Date().toISOString();
const hoursAgo = (h: number) => new Date(Date.now() - h * 3600 * 1000).toISOString();

const defaultPrograms = (): IrrigationProgram[] => [
  {
    id: 'prog-001',
    name: 'Irrigation matinale',
    description: 'Arrosage du matin et du soir',
    zone_id: 'zone-a1',
    type: 'irrigation',
    mode: 'weekly',
    days: [0, 1, 2, 3, 4],
    cycle_days: 2,
    time_ranges: [
      { id: 'tr-1', start: '06:00', end: '07:00' },
      { id: 'tr-2', start: '18:00', end: '18:45' },
    ],
    enabled: true,
    created_at: hoursAgo(72),
    last_run: hoursAgo(14),
  },
  {
    id: 'prog-002',
    name: 'Fertigation hebdo',
    description: 'Apport nutritif lundi et jeudi',
    zone_id: 'zone-b1',
    type: 'fertigation',
    mode: 'weekly',
    days: [0, 3],
    cycle_days: 3,
    time_ranges: [{ id: 'tr-3', start: '08:00', end: '09:00' }],
    enabled: true,
    created_at: hoursAgo(120),
    last_run: hoursAgo(40),
  },
  {
    id: 'prog-003',
    name: 'Cycle estival',
    description: 'Cycle tous les 2 jours',
    zone_id: 'zone-c1',
    type: 'irrigation',
    mode: 'cyclic',
    days: [],
    cycle_days: 2,
    time_ranges: [
      { id: 'tr-4', start: '06:20', end: '08:05' },
      { id: 'tr-5', start: '19:00', end: '21:35' },
    ],
    enabled: false,
    created_at: hoursAgo(200),
    last_run: hoursAgo(60),
  },
];

const defaultRuntime = (): Record<string, ZoneRuntime> => {
  const map: Record<string, ZoneRuntime> = {};
  const seedHours: Record<string, number> = {
    'zone-a1': 14,
    'zone-a2': 50,
    'zone-b1': 40,
    'zone-b2': 96,
    'zone-c1': 60,
  };
  ZONES.forEach((z) => {
    map[z.id] = { last_irrigated_at: hoursAgo(seedHours[z.id] ?? 24), session: null };
  });
  return map;
};

const defaultHistory = (): HistoryEvent[] => {
  const mk = (
    zone: IrrigationZone,
    kind: 'manual' | 'scheduled',
    startH: number,
    durMin: number,
    type: ProgramType,
    programName?: string
  ): HistoryEvent => {
    const started = new Date(Date.now() - startH * 3600 * 1000);
    const ended = new Date(started.getTime() + durMin * 60 * 1000);
    return {
      id: newId('hist'),
      zone_id: zone.id,
      zone_name: zone.name,
      field_name: zone.field_name,
      kind,
      program_name: programName,
      type,
      started_at: started.toISOString(),
      ended_at: ended.toISOString(),
      duration_minutes: durMin,
      water_liters: Math.round(durMin * zone.flow_rate_lpm),
      status: 'completed',
    };
  };
  return [
    mk(ZONES[0], 'scheduled', 14, 60, 'irrigation', 'Irrigation matinale'),
    mk(ZONES[2], 'scheduled', 40, 60, 'fertigation', 'Fertigation hebdo'),
    mk(ZONES[1], 'manual', 50, 25, 'irrigation'),
    mk(ZONES[4], 'scheduled', 60, 105, 'irrigation', 'Cycle estival'),
    mk(ZONES[0], 'manual', 90, 18, 'irrigation'),
  ].sort((a, b) => new Date(b.started_at).getTime() - new Date(a.started_at).getTime());
};

// ---- Persistence ----
const STORAGE_KEY = 'agrisafe_irrigation_v1';
interface PersistShape {
  programs: IrrigationProgram[];
  runtime: Record<string, ZoneRuntime>;
  history: HistoryEvent[];
}
const loadState = (): PersistShape => {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (raw) {
      const parsed = JSON.parse(raw);
      if (parsed.programs && parsed.runtime && parsed.history) return parsed;
    }
  } catch {
    /* ignore */
  }
  return { programs: defaultPrograms(), runtime: defaultRuntime(), history: defaultHistory() };
};

// ---- Context ----
interface IrrigationContextValue {
  zones: IrrigationZone[];
  programs: IrrigationProgram[];
  runtime: Record<string, ZoneRuntime>;
  history: HistoryEvent[];
  now: number;
  zoneById: (id: string) => IrrigationZone | undefined;
  addProgram: (p: Omit<IrrigationProgram, 'id' | 'created_at'>) => void;
  updateProgram: (id: string, p: Omit<IrrigationProgram, 'id' | 'created_at'>) => void;
  deleteProgram: (id: string) => void;
  toggleProgram: (id: string) => void;
  runProgramNow: (id: string) => void;
  startManual: (zoneId: string) => void;
  stopManual: (zoneId: string) => void;
}

const IrrigationContext = createContext<IrrigationContextValue | undefined>(undefined);

export const IrrigationProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const initial = useRef(loadState());
  const [programs, setPrograms] = useState<IrrigationProgram[]>(initial.current.programs);
  const [runtime, setRuntime] = useState<Record<string, ZoneRuntime>>(initial.current.runtime);
  const [history, setHistory] = useState<HistoryEvent[]>(initial.current.history);
  const [now, setNow] = useState<number>(Date.now());

  // Persist
  useEffect(() => {
    localStorage.setItem(STORAGE_KEY, JSON.stringify({ programs, runtime, history }));
  }, [programs, runtime, history]);

  const zoneById = useCallback((id: string) => ZONES.find((z) => z.id === id), []);

  // Tick + finalise les sessions planifiées arrivées à terme
  useEffect(() => {
    const interval = setInterval(() => {
      const t = Date.now();
      setNow(t);
      setRuntime((prev) => {
        let changed = false;
        const next: Record<string, ZoneRuntime> = { ...prev };
        const finished: HistoryEvent[] = [];
        Object.entries(prev).forEach(([zid, rt]) => {
          if (rt.session && rt.session.kind === 'scheduled' && rt.session.ends_at) {
            if (new Date(rt.session.ends_at).getTime() <= t) {
              const zone = ZONES.find((z) => z.id === zid);
              const startMs = new Date(rt.session.started_at).getTime();
              const endMs = new Date(rt.session.ends_at).getTime();
              const durMin = Math.max(1, Math.round((endMs - startMs) / 60000));
              if (zone) {
                finished.push({
                  id: newId('hist'),
                  zone_id: zid,
                  zone_name: zone.name,
                  field_name: zone.field_name,
                  kind: 'scheduled',
                  program_name: rt.session.program_name,
                  type: rt.session.type,
                  started_at: rt.session.started_at,
                  ended_at: rt.session.ends_at,
                  duration_minutes: durMin,
                  water_liters: Math.round(durMin * zone.flow_rate_lpm),
                  status: 'completed',
                });
              }
              next[zid] = { last_irrigated_at: rt.session.ends_at, session: null };
              changed = true;
            }
          }
        });
        if (finished.length) {
          setHistory((h) => [...finished, ...h]);
        }
        return changed ? next : prev;
      });
    }, 1000);
    return () => clearInterval(interval);
  }, []);

  const addProgram: IrrigationContextValue['addProgram'] = useCallback((p) => {
    setPrograms((prev) => [{ ...p, id: newId('prog'), created_at: nowIso() }, ...prev]);
  }, []);

  const updateProgram: IrrigationContextValue['updateProgram'] = useCallback((id, p) => {
    setPrograms((prev) => prev.map((x) => (x.id === id ? { ...x, ...p, id, created_at: x.created_at } : x)));
  }, []);

  const deleteProgram: IrrigationContextValue['deleteProgram'] = useCallback((id) => {
    setPrograms((prev) => prev.filter((x) => x.id !== id));
  }, []);

  const toggleProgram: IrrigationContextValue['toggleProgram'] = useCallback((id) => {
    setPrograms((prev) => prev.map((x) => (x.id === id ? { ...x, enabled: !x.enabled } : x)));
  }, []);

  const runProgramNow: IrrigationContextValue['runProgramNow'] = useCallback(
    (id) => {
      const prog = programs.find((p) => p.id === id);
      if (!prog) return;
      const durMin = Math.max(1, totalDailyMinutes(prog.time_ranges));
      const started = new Date();
      const ends = new Date(started.getTime() + durMin * 60 * 1000);
      setRuntime((prev) => ({
        ...prev,
        [prog.zone_id]: {
          last_irrigated_at: prev[prog.zone_id]?.last_irrigated_at ?? null,
          session: {
            kind: 'scheduled',
            program_id: prog.id,
            program_name: prog.name,
            type: prog.type,
            started_at: started.toISOString(),
            ends_at: ends.toISOString(),
          },
        },
      }));
      setPrograms((prev) => prev.map((p) => (p.id === id ? { ...p, last_run: started.toISOString() } : p)));
    },
    [programs]
  );

  const startManual: IrrigationContextValue['startManual'] = useCallback((zoneId) => {
    setRuntime((prev) => ({
      ...prev,
      [zoneId]: {
        last_irrigated_at: prev[zoneId]?.last_irrigated_at ?? null,
        session: { kind: 'manual', type: 'irrigation', started_at: new Date().toISOString() },
      },
    }));
  }, []);

  const stopManual: IrrigationContextValue['stopManual'] = useCallback((zoneId) => {
    setRuntime((prev) => {
      const rt = prev[zoneId];
      if (!rt || !rt.session) return prev;
      const zone = ZONES.find((z) => z.id === zoneId);
      const startMs = new Date(rt.session.started_at).getTime();
      const endMs = Date.now();
      const durMin = Math.max(1, Math.round((endMs - startMs) / 60000));
      if (zone) {
        const ev: HistoryEvent = {
          id: newId('hist'),
          zone_id: zoneId,
          zone_name: zone.name,
          field_name: zone.field_name,
          kind: rt.session.kind,
          program_name: rt.session.program_name,
          type: rt.session.type,
          started_at: rt.session.started_at,
          ended_at: new Date(endMs).toISOString(),
          duration_minutes: durMin,
          water_liters: Math.round(durMin * zone.flow_rate_lpm),
          status: 'stopped',
        };
        setHistory((h) => [ev, ...h]);
      }
      return { ...prev, [zoneId]: { last_irrigated_at: new Date(endMs).toISOString(), session: null } };
    });
  }, []);

  const value: IrrigationContextValue = {
    zones: ZONES,
    programs,
    runtime,
    history,
    now,
    zoneById,
    addProgram,
    updateProgram,
    deleteProgram,
    toggleProgram,
    runProgramNow,
    startManual,
    stopManual,
  };

  return <IrrigationContext.Provider value={value}>{children}</IrrigationContext.Provider>;
};

export const useIrrigation = (): IrrigationContextValue => {
  const ctx = useContext(IrrigationContext);
  if (!ctx) throw new Error('useIrrigation must be used within IrrigationProvider');
  return ctx;
};
